import {
  getLibraryAdminSession,
  refreshLibraryAdminToken,
} from "@libraryadmin/services/auth";
import { cmsFetch, getAdminSession } from "@jambushrushti/services/adminAuth";
import { canManagePortalUsers } from "@/services/jambushrustiAuth";
import { apiErrorMessage } from "@/services/apiErrorMessage";

export type PortalRole = "cms" | "library" | "admin";
export interface ManagedUser {
  id: string;
  email: string;
  role: PortalRole | "unknown";
  is_active: boolean | null;
}
const base = import.meta.env.DEV
  ? "/jambushrusti-api"
  : (
      import.meta.env.VITE_API_BASE_URL ??
      "https://api.jambushusti.universal-software.in/api"
    ).replace(/\/$/, "");
const root = `${base}/jambushrusti/auth`;

async function request(
  path: string,
  options: RequestInit = {},
  portal: "cms" | "library" = "cms"
) {
  const session =
    portal === "library" ? getLibraryAdminSession() : getAdminSession();
  if (!canManagePortalUsers(session)) {
    throw new Error("Only Admin accounts can manage users.");
  }
  const libraryRequest = (token: string) =>
    fetch(root + path, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        "X-Tenant-Id":
          import.meta.env.VITE_X_TENANTID ??
          "6cf833d7-bf6f-4688-9136-5674d8053b85",
        Authorization: "Bearer " + token,
      },
    });
  let response =
    portal === "library"
      ? await libraryRequest(session!.accessToken)
      : await cmsFetch(root + path, options);
  if (
    portal === "library" &&
    response.status === 401 &&
    session?.refreshToken
  ) {
    response = await libraryRequest(await refreshLibraryAdminToken());
  }
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      apiErrorMessage(
        payload,
        `Unable to complete the request (${response.status}).`
      )
    );
  }
  return payload;
}
function normalize(value: Record<string, unknown>): ManagedUser {
  if (value.id == null || typeof value.email !== "string") {
    throw new Error("The server returned an invalid user record.");
  }
  return {
    id: String(value.id),
    email: value.email,
    role: ["cms", "library", "admin"].includes(String(value.role))
      ? (value.role as PortalRole)
      : "unknown",
    is_active: typeof value.is_active === "boolean" ? value.is_active : null,
  };
}
export async function listUsers(
  includeInactive: boolean,
  portal: "cms" | "library" = "cms"
): Promise<ManagedUser[]> {
  const payload = await request(
    `/users/${includeInactive ? "?include_inactive=true" : ""}`,
    {},
    portal
  );
  const data = payload.data ?? payload;
  const rows = data.users ?? data.results ?? data;
  if (!Array.isArray(rows)) {
    throw new Error("The server returned an invalid user list.");
  }
  const users = rows.map(normalize);
  return users.filter(user => user.role === portal);
}
export async function getUser(
  id: string,
  portal: "cms" | "library" = "cms"
): Promise<ManagedUser> {
  const payload = await request(
    `/users/${encodeURIComponent(id)}/`,
    {},
    portal
  );
  const data = payload.data ?? payload;
  return normalize(data.user ?? data);
}
export async function createUser(
  email: string,
  password: string,
  role: PortalRole,
  portal: "cms" | "library" = "cms"
): Promise<void> {
  await request(
    "/signup/",
    {
      method: "POST",
      body: JSON.stringify({ email, password, role }),
    },
    portal
  );
}
export async function updateRole(
  id: string,
  role: PortalRole,
  portal: "cms" | "library" = "cms"
): Promise<void> {
  await request(
    `/users/${encodeURIComponent(id)}/role/`,
    {
      method: "PATCH",
      body: JSON.stringify({ role }),
    },
    portal
  );
}
export async function deleteUser(
  id: string,
  portal: "cms" | "library" = "cms"
): Promise<void> {
  await request(
    `/users/${encodeURIComponent(id)}/`,
    { method: "DELETE" },
    portal
  );
}
